/**
 * predictive-search.js
 *
 * <predictive-search> custom element. Hooks the search drawer input and
 * fetches Shopify's /search/suggest?section_id=predictive-search&q=... on
 * keystroke (debounced), injecting the resulting HTML fragment into the
 * #predictive-search-results container.
 *
 * Replaces the ~1,500 lines of Shopify legacy PredictiveSearch /
 * PredictiveSearchComponent / SearchHeader code in theme.js — which was
 * non-functional anyway (the old code expected a mount point that didn't
 * exist in the markup).
 *
 * Required DOM:
 *   <predictive-search ...>
 *     <input data-predictive-search-input type="search" name="q" ...>
 *     <div id="predictive-search-results" data-predictive-search-results></div>
 *   </predictive-search>
 *
 * Configuration via attributes:
 *   data-resources-type  → comma-separated list (default
 *     "product,query,article,page,collection"). Limits which result groups
 *     come back from Shopify.
 *   data-min-chars       → minimum query length before fetching (default 2)
 *   data-limit           → results per type (default 4)
 *
 * No dependency on theme.js, vendor.js, jQuery, lodash. Pure vanilla. Coexists
 * with everything else — works even if app.js is removed in the future.
 */
class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('[data-predictive-search-input]');
    this.resultsContainer = this.querySelector('[data-predictive-search-results]');
    this.statusElement = this.querySelector('[data-predictive-search-status]');

    if (!this.input || !this.resultsContainer) {
      console.warn('<predictive-search>: missing input or results container');
      return;
    }

    this.resourcesType = this.dataset.resourcesType || 'product,query,article,page,collection';
    this.minChars = parseInt(this.dataset.minChars, 10) || 2;
    // Shopify's predictive search API caps at 10 per resource type. Default
    // to the cap so customers see as many matches as Shopify is willing to
    // surface. Override per-instance with data-limit="N" on <predictive-search>.
    this.limit = parseInt(this.dataset.limit, 10) || 10;
    this.debounceMs = 300;

    this.activeIndex = -1;
    this.currentRequest = null;
    this.lastFetchedQuery = '';

    this.boundInput = this.debounce(this.onInput.bind(this), this.debounceMs);
    this.boundKeydown = this.onKeydown.bind(this);
    this.boundFocus = this.onFocus.bind(this);
    this.boundBlur = this.onBlur.bind(this);
    this.boundDocumentClick = this.onDocumentClick.bind(this);

    this.input.addEventListener('input', this.boundInput);
    this.input.addEventListener('keydown', this.boundKeydown);
    this.input.addEventListener('focus', this.boundFocus);
    this.input.addEventListener('blur', this.boundBlur);
    document.addEventListener('click', this.boundDocumentClick);
  }

  disconnectedCallback() {
    if (!this.input) return;
    this.input.removeEventListener('input', this.boundInput);
    this.input.removeEventListener('keydown', this.boundKeydown);
    this.input.removeEventListener('focus', this.boundFocus);
    this.input.removeEventListener('blur', this.boundBlur);
    document.removeEventListener('click', this.boundDocumentClick);
    if (this.currentRequest) this.currentRequest.abort();
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  onInput() {
    const query = this.input.value.trim();
    if (query.length < this.minChars) {
      this.close();
      return;
    }
    if (query === this.lastFetchedQuery) return;
    this.fetch(query);
  }

  fetch(query) {
    if (this.currentRequest) this.currentRequest.abort();

    const controller = new AbortController();
    this.currentRequest = controller;

    const url =
      window.routes.predictive_search_url +
      '?q=' +
      encodeURIComponent(query) +
      '&section_id=predictive-search' +
      '&resources[type]=' +
      encodeURIComponent(this.resourcesType) +
      '&resources[limit]=' +
      this.limit;

    this.setLoading(true);

    fetch(url, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error('Predictive search fetch failed: ' + response.status);
        return response.text();
      })
      .then((html) => {
        // Response is the FULL section HTML (the entire section wrapped in
        // its <div class="shopify-section">). Extract the inner content.
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const inner =
          doc.querySelector('#predictive-search-results-list') ||
          doc.querySelector('.shopify-section');
        if (inner) {
          this.resultsContainer.innerHTML = inner.outerHTML;
        } else {
          this.resultsContainer.innerHTML = html;
        }
        this.lastFetchedQuery = query;
        this.activeIndex = -1;
        this.open();
        this.announceResults();
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        console.error(err);
      })
      .finally(() => {
        this.setLoading(false);
        if (this.currentRequest === controller) this.currentRequest = null;
      });
  }

  open() {
    this.classList.add('is-open');
    this.resultsContainer.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.classList.remove('is-open');
    this.resultsContainer.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.activeIndex = -1;
  }

  setLoading(loading) {
    this.classList.toggle('is-loading', loading);
    if (this.statusElement) {
      this.statusElement.textContent = loading ? 'Loading...' : '';
    }
  }

  announceResults() {
    if (!this.statusElement) return;
    const countEl = this.resultsContainer.querySelector('[data-predictive-search-result-count]');
    if (countEl) {
      const count = parseInt(countEl.textContent, 10) || 0;
      this.statusElement.textContent =
        count === 0
          ? 'No results found'
          : count + ' result' + (count === 1 ? '' : 's') + ' available';
    }
  }

  // ---- Keyboard navigation ----
  getOptions() {
    return Array.from(this.resultsContainer.querySelectorAll('[role="option"]'));
  }

  onKeydown(event) {
    if (!this.classList.contains('is-open')) return;

    const options = this.getOptions();
    if (options.length === 0) return;

    switch (event.code) {
      case 'ArrowDown':
        event.preventDefault();
        this.activeIndex = (this.activeIndex + 1) % options.length;
        this.updateActive(options);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.activeIndex = this.activeIndex <= 0 ? options.length - 1 : this.activeIndex - 1;
        this.updateActive(options);
        break;
      case 'Enter':
        if (this.activeIndex >= 0) {
          event.preventDefault();
          const link = options[this.activeIndex].querySelector('a') || options[this.activeIndex];
          if (link && link.href) {
            window.location.href = link.href;
          }
        }
        // If no active option, let the form submit normally
        break;
      case 'Escape':
        event.preventDefault();
        this.close();
        this.input.blur();
        break;
    }
  }

  updateActive(options) {
    options.forEach((opt, i) => {
      const isActive = i === this.activeIndex;
      opt.setAttribute('aria-selected', isActive ? 'true' : 'false');
      opt.classList.toggle('is-active', isActive);
      if (isActive) {
        this.input.setAttribute('aria-activedescendant', opt.id);
        opt.scrollIntoView({ block: 'nearest' });
      }
    });
  }

  onFocus() {
    // Re-open if the user re-focuses with existing query content
    const query = this.input.value.trim();
    if (query.length >= this.minChars && this.lastFetchedQuery === query) {
      this.open();
    }
  }

  onBlur() {
    // Don't close immediately; click on a result needs to fire first.
    // Document-click handler manages outside-click close.
  }

  onDocumentClick(event) {
    if (!this.contains(event.target)) {
      this.close();
    }
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
